import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { AssignmentData, User } from "../types/site-visit";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";
import CustomTextField from "../../../components/common/Textfield";
import apiService from "../../../service/Api";
import useDebounce from "../../../hooks/useDebounce";

interface AssignmentFormProps {
  onSubmit: (data: AssignmentData) => void;
  onCancel: () => void;
  loading?: boolean;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState<string>("");
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [assigneeOptions, setAssigneeOptions] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch.trim().length < 2) return;

    const fetchUsers = async () => {
      setIsSearching(true);
      try {
        const response = await apiService.get<{ results: User[] }>(
          "/api/crews/crew-members/",
          {
            search: debouncedSearch,
          }
        );
        setAssigneeOptions(response.data.results || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setAssigneeOptions([]);
      } finally {
        setIsSearching(false);
      }
    };

    fetchUsers();
  }, [debouncedSearch]);

  const handleSubmit = () => {
    if (assigneeId && dueDate) {
      onSubmit({
        assigneeId,
        dueDate: dueDate.toISOString(),
        notes,
      });
    }
  };

  const isFormValid = assigneeId && dueDate;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Autocomplete
          fullWidth
          options={assigneeOptions}
          getOptionLabel={(option) =>
            option.user
              ? `${option.user.first_name} ${option.user.last_name}`
              : ""
          }
          loading={isSearching}
          onInputChange={(_event, newValue) => {
            setSearchInput(newValue);
          }}
          onChange={(_event, newValue) => {
            setAssigneeId(newValue?.user?.id || "");
          }}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              label="Search Assignee"
              variant="outlined"
              helperText="Type 3 letters to search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <CustomTextField
          label="Due Date"
          value={dueDate ? dueDate.toISOString().split("T")[0] : ""}
          type="date"
          InputLabelProps={{ shrink: true }}
          onChange={(e) => setDueDate(new Date(e.target.value))}
        />
      </Box>

      <Box>
        <Typography variant="body1" sx={{ mb: 1, fontWeight: "medium" }}>
          Notes/Instructions
        </Typography>
        <CustomTextField
          fullWidth
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Select person for this site visit"
          variant="outlined"
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{
            px: 4,
            backgroundColor: reduxTheme.primary_colors[800],
            color: reduxTheme.primary_colors[100],
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
          sx={{
            px: 4,
            backgroundColor: reduxTheme.primary_colors[400],
          }}
        >
          {loading ? "Assigning..." : "Assign"}
        </Button>
      </Box>
    </Box>
  );
};

export default AssignmentForm;
